import React, { useEffect, useState, useCallback, useRef } from "react";
import { Modal, Spin, Steps, Typography } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import {
  StepsContainer,
  IframeContainer,
  SpinnerContainer,
  TimeoutMessage,
} from "./three-ds-modal.styled";

const { Text } = Typography;

const TIMEOUT_SECONDS = 120;

interface ThreeDSModalProps {
  open: boolean;
  content?: Record<string, string | undefined> | null;
  iframeContent: string | null;
  isCompletingPayment: boolean;
}

function ThreeDSModal({
  open,
  content,
  iframeContent,
  isCompletingPayment,
}: ThreeDSModalProps) {
  const [callbackDetected, setCallbackDetected] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const iframeLoadCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      setCallbackDetected(false);
      setTimedOut(false);
      iframeLoadCount.current = 0;
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open]);

  useEffect(() => {
    if (open && iframeContent) {
      timerRef.current = setTimeout(() => {
        setTimedOut(true);
      }, TIMEOUT_SECONDS * 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, iframeContent]);

  const handleIframeLoad = useCallback(() => {
    iframeLoadCount.current += 1;
    if (iframeLoadCount.current > 1) {
      setCallbackDetected(true);
    }
  }, []);

  const currentStep = isCompletingPayment || callbackDetected ? 2 : 1;

  const spinText =
    callbackDetected || isCompletingPayment
      ? content?.threeds_completing_payment || "Completando pago, por favor espere..."
      : content?.threeds_verifying_bank || "Verificando con su banco...";

  return (
    <Modal
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SafetyCertificateOutlined style={{ color: "#52c41a", fontSize: 20 }} />
          {content?.threeds_modal_title || "Verificación de seguridad (3D Secure)"}
        </span>
      }
      open={open}
      footer={null}
      closable={false}
      width={640}
      centered
      maskClosable={false}
    >
      <StepsContainer>
        <Steps
          current={currentStep}
          items={[
            { title: content?.threeds_step_card_data || "Datos de tarjeta", status: "finish" },
            {
              title: content?.threeds_step_bank_verification || "Verificación bancaria",
              status: currentStep === 1 ? "process" : "finish",
            },
            {
              title: content?.threeds_step_processing || "Procesando pago",
              status: currentStep === 2 ? "process" : "wait",
            },
          ]}
        />
      </StepsContainer>

      {iframeContent && (
        <IframeContainer>
          <iframe
            srcDoc={iframeContent}
            style={{ width: "100%", height: 480, border: "none", display: "block" }}
            title="3DS Challenge"
            onLoad={handleIframeLoad}
          />
        </IframeContainer>
      )}

      {timedOut ? (
        <TimeoutMessage>
          <Text type="warning" strong style={{ fontSize: 14 }}>
            {content?.threeds_timeout_message ||
              "La verificación está tomando más tiempo del esperado. Por favor, comuníquese con el departamento financiero o intente nuevamente."}
          </Text>
        </TimeoutMessage>
      ) : (
        <SpinnerContainer>
          <Spin size="large" />
          <Text type="secondary" style={{ fontSize: 14 }}>
            {spinText}
          </Text>
        </SpinnerContainer>
      )}
    </Modal>
  );
}

export default ThreeDSModal;
